import type { RoleFieldKey, RoleFieldVisibility, SiteConfig } from '../interfaces/ConfigInterface';
import type { RoleInfo, RoleWriteInput } from '../interfaces/EmployeeInfoInterface';

export type { RoleFieldKey, RoleFieldVisibility };

/**
 * Employee Roles page (TAS-668): which Role switches a tenant renders.
 *
 * `config.roleFieldVisibility[key]` (true/false) wins. An absent key falls back to the
 * tenant flags that already gated these fields in the old RoleModal; a key with no
 * fallback is shown. The one exception is `admin` ("Auto-create Admin App login"),
 * which only the NPM sync reads, so it is shown only when configured explicitly.
 *
 * Hidden fields are never sent on an update (the API writes only the flag keys it
 * receives), so hiding a field can never change its stored value.
 */

/** Every Role switch the page knows, in editor order. */
export const ROLE_FIELD_KEYS: ReadonlyArray<RoleFieldKey> = [
  'allowMobileApp', 'kioskUser', 'allowAdminAppEmployee',
  'recordHours', 'recordHoursJob', 'recordHoursJobAcctCodeReq', 'recordHoursOther',
  'recordHoursServiceOrder', 'recordHoursCustomerServiceText', 'recordHoursCustomerServiceAndWarranty',
  'recordTasks', 'recordOperationalTask', 'recordCustomerServiceWarranty',
  'recordPhoto', 'recordLocation', 'useClockHourlyV2', 'recordMealHoursOnly', 'allowTimeGap',
  'allowTimeOffRequests', 'recordSick',
  'allowOnCallPay', 'requireProductionPay',
  'office', 'supervisor', 'admin',
];

/** The config fields the resolver reads (a narrow Pick keeps the unit test simple). */
export type RoleVisibilityConfig = Pick<
  SiteConfig,
  'roleFieldVisibility' | 'onCallPay' | 'productionPay' | 'roleFeatureToggles' | 'showKioskDevicesPage' | 'kioskCompanyIDs'
>;

const FALLBACKS: Partial<Record<RoleFieldKey, (c: RoleVisibilityConfig) => boolean>> = {
  allowOnCallPay: (c) => c.onCallPay?.enabled === true,
  requireProductionPay: (c) => c.productionPay?.enabled === true,
  useClockHourlyV2: (c) => c.roleFeatureToggles?.clockHourlyV2 === true,
  allowTimeOffRequests: (c) => c.roleFeatureToggles?.timeOffRequests === true,
  kioskUser: (c) => c.showKioskDevicesPage === true || (c.kioskCompanyIDs?.length ?? 0) > 0,
  admin: () => false,
};

/** True when the tenant renders this Role field. */
export function isRoleFieldVisible(config: RoleVisibilityConfig, key: RoleFieldKey): boolean {
  const explicit = config.roleFieldVisibility?.[key];
  if (typeof explicit === 'boolean') return explicit;
  const fallback = FALLBACKS[key];
  return fallback ? fallback(config) : true;
}

/** The full visibility map for a tenant. */
export function resolveRoleFieldVisibility(config: RoleVisibilityConfig): Record<RoleFieldKey, boolean> {
  const out = {} as Record<RoleFieldKey, boolean>;
  ROLE_FIELD_KEYS.forEach((k) => { out[k] = isRoleFieldVisible(config, k); });
  return out;
}

/** A blank new role: Mobile App on, every other switch off. */
export function blankRoleFlags(): Record<RoleFieldKey, boolean> {
  const out = {} as Record<RoleFieldKey, boolean>;
  ROLE_FIELD_KEYS.forEach((k) => { out[k] = false; });
  out.allowMobileApp = true;
  return out;
}

type RoleSource = Pick<RoleInfo, 'name' | 'stateAbbr' | 'active' | 'accountCodes' | 'locations'>
  & Partial<Record<RoleFieldKey, boolean | undefined>>;

/**
 * The PUT / POST body for a role.
 * - `update`: name, stateAbbr, active, accountCodes, locations and the VISIBLE switches only.
 * - `create`: every switch, always including allowMobileApp. POST writes an unsent flag
 *   as 0, so a create must carry the flags explicitly; a duplicate copies all of the
 *   source's switches, hidden ones included (omitting them would zero them anyway).
 */
export function buildRoleWritePayload(
  role: RoleSource,
  visibility: Record<RoleFieldKey, boolean>,
  mode: 'update' | 'create',
): RoleWriteInput {
  const payload: RoleWriteInput = {
    name: (role.name ?? '').trim(),
    stateAbbr: role.stateAbbr ?? '',
    active: !!role.active,
    accountCodes: [...(role.accountCodes ?? [])],
    locations: [...(role.locations ?? [])],
  };
  ROLE_FIELD_KEYS.forEach((k) => {
    if (mode === 'create' || visibility[k]) payload[k] = !!role[k];
  });
  return payload;
}
